class NoelCategoryNav extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ne-category-track');
    this.controls = this.querySelector('.ne-category-controls');
    if (!this.track || !this.controls) return;
    this.abort?.abort();
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.track.querySelectorAll('.ne-category-image img').forEach(image => {
      const fallback = () => {
        image.closest('.ne-category-tile')?.classList.add('ne-category-tile--text');
        image.closest('.ne-category-image')?.remove();
      };
      image.addEventListener('error', fallback, options);
      if (image.complete && !image.naturalWidth) fallback();
    });
    const update = () => {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.controls.hidden = max < 2;
      this.controls.querySelector('[data-direction="-1"]').disabled = this.track.scrollLeft <= 1;
      this.controls.querySelector('[data-direction="1"]').disabled = this.track.scrollLeft >= max - 1;
    };
    this.controls.querySelectorAll('button').forEach(button => button.addEventListener('click', () => {
      this.track.scrollBy({ left: Number(button.dataset.direction) * this.track.clientWidth * .8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, options));
    this.track.addEventListener('scroll', update, { ...options, passive: true });
    this.resize?.disconnect();
    this.resize = new ResizeObserver(update);
    this.resize.observe(this.track);
    update();
  }
  disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
}
if (!customElements.get('noel-category-nav')) customElements.define('noel-category-nav', NoelCategoryNav);

class NoelDocument extends HTMLElement {
  connectedCallback() { this.setup(); }
  setup() {
    const body = this.querySelector('.ne-document-body');
    const layout = this.querySelector('.ne-reading-layout');
    if (!body || !layout) return;
    this.observer?.disconnect();
    layout.querySelector(':scope > .ne-toc')?.remove();
    body.querySelectorAll('h1').forEach(heading => {
      const replacement = document.createElement('h2');
      for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
      replacement.append(...heading.childNodes);
      heading.replaceWith(replacement);
    });
    const headings = Array.from(body.querySelectorAll('h2,h3')).filter(h => h.textContent.trim());
    this.classList.toggle('has-toc', headings.length > 0);
    if (!headings.length) return;
    const nav = document.createElement('nav');
    nav.className = 'ne-toc';
    nav.setAttribute('aria-label', this.dataset.tocLabel || 'Op deze pagina');
    const label = document.createElement('p');
    label.className = 'ne-toc-title';
    label.textContent = nav.getAttribute('aria-label');
    const links = document.createElement('div');
    links.className = 'ne-toc-links';
    nav.append(label, links);
    const linkMap = new Map();
    headings.forEach((heading, index) => {
      if (!heading.id || document.getElementById(heading.id) !== heading) {
        const stem = heading.textContent.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 64) || 'onderwerp';
        let id = `noel-${stem}`;
        let suffix = 2;
        while (document.getElementById(id)) id = `noel-${stem}-${suffix++}`;
        heading.id = id;
      }
      const link = document.createElement('a');
      link.href = `#${encodeURIComponent(heading.id)}`;
      if (heading.tagName === 'H3') link.className = 'ne-toc-subheading';
      const number = document.createElement('span');
      number.setAttribute('aria-hidden', 'true');
      number.textContent = String(index + 1).padStart(2, '0');
      link.append(number, document.createTextNode(heading.textContent.trim()));
      links.append(link);
      linkMap.set(heading, link);
    });
    layout.prepend(nav);
    const activate = heading => {
      links.querySelector('[aria-current]')?.removeAttribute('aria-current');
      linkMap.get(heading)?.setAttribute('aria-current', 'location');
    };
    activate(headings[0]);
    const visible = new Set();
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target));
      const first = headings.find(heading => visible.has(heading));
      if (first) activate(first);
    }, { rootMargin: '-15% 0px -50% 0px' });
    headings.forEach(h => this.observer.observe(h));
  }
  disconnectedCallback() { this.observer?.disconnect(); }
}
if (!customElements.get('noel-document')) customElements.define('noel-document', NoelDocument);

function enhancePolicies() {
  document.querySelectorAll('.shopify-policy__container:not([data-noel-ready])').forEach(container => {
    const body = container.querySelector('.shopify-policy__body');
    if (!body) return;
    container.dataset.noelReady = 'true';
    const script = document.querySelector('script[data-noel-toc-label]');
    const wrapper = document.createElement('noel-document');
    wrapper.className = 'ne-document ne-shell';
    wrapper.dataset.tocLabel = script?.dataset.noelTocLabel || 'Op deze pagina';
    const title = container.querySelector('.shopify-policy__title');
    if (title) wrapper.append(title);
    const layout = document.createElement('div');
    layout.className = 'ne-reading-layout';
    body.classList.add('ne-document-body', 'ne-richtext');
    layout.append(body);
    wrapper.append(layout);
    container.append(wrapper);
    wrapper.setup();
  });
}
enhancePolicies();
document.addEventListener('shopify:section:load', enhancePolicies);

document.addEventListener('click', event => {
  const link = event.target.closest?.('.ne-collection-head a[href="#NoelCollectionStory"]');
  if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  const description = document.getElementById('NoelCollectionStory')?.querySelector('details');
  if (description) description.open = true;
});
